import { useAuth } from '@eti/authentication';
import { useProperty, useSiteContext } from '@eti/providers';
import { device } from '@eti/utils';
import { useEffect, useMemo } from 'react';
import {
  closeZendeskChat,
  initZendesk,
  openZendeskChat,
  applyZendeskIdentity,
} from './zendeskUtils';

export type ChatExtraInfo = { id: string; value: any }[];
export type ChatUserInfo = { name: string; firstName: string; lastName: string };

export const useChat = (
  userInfo?: ChatUserInfo,
  userEmail?: string | null,
  orderNumber?: string | null,
  shouldCloseOnUnmount = false,
) => {
  const { p } = useProperty();
  const { user } = useAuth();
  const { language, market, siteName } = useSiteContext();
  const isZendeskEnabled = Boolean(p('Chat.Zendesk.Enabled'));
  const deploymentKey = p('Chat.Zendesk.DeploymentKey');

  const extraInfo = useMemo(
    (): ChatExtraInfo => [
      { id: p('Zendesk.ConversationField.Browser.Id'), value: device.browserType() },
      { id: p('Zendesk.ConversationField.UserEmail.Id'), value: userEmail || user?.email || '' },
      { id: p('Zendesk.ConversationField.UserName.Id'), value: userInfo?.name || '' },
      {
        id: p('Zendesk.ConversationField.OrderNumber.Id'),
        value: orderNumber || user?.sessionOrderNumber || '',
      },
      { id: p('Zendesk.ConversationField.OS.Id'), value: device.osType() },
      {
        id: p('Zendesk.ConversationField.Source.Id'),
        value: `zdm_source__${device.isOfType().toLowerCase()}`,
      },
      { id: p('Zendesk.ConversationField.SiteName.Id'), value: `zdm_site__${siteName}` },
      {
        id: p('Zendesk.ConversationField.Language.Id'),
        value: `zdm_language__${language.code}`,
      },
      { id: p('Zendesk.ConversationField.Url.Id'), value: window.location.href },
      {
        id: p('Zendesk.ConversationField.CountryCode.Id'),
        value: `zdm_country_code__${market.code.toLowerCase()}`,
      },
      {
        id: p('Zendesk.ConversationField.BrowserLanguage.Id'),
        value: `zdm_browser_language__${navigator.language.toLowerCase()}`,
      },
      { id: p('Zendesk.ConversationField.FirstName.Id'), value: userInfo?.firstName || '' },
      { id: p('Zendesk.ConversationField.LastName.Id'), value: userInfo?.lastName || '' },
    ],
    [
      language.code,
      market.code,
      orderNumber,
      p,
      siteName,
      userInfo,
      user?.email,
      user?.sessionOrderNumber,
      userEmail,
    ],
  );

  useEffect(() => {
    if (!isZendeskEnabled) {
      return;
    }

    if (userInfo !== undefined) {
      initZendesk(language.code, extraInfo, deploymentKey);
    }
    applyZendeskIdentity(extraInfo);

    return () => {
      if (shouldCloseOnUnmount) {
        closeZendeskChat();
      }
    };
  }, [isZendeskEnabled, deploymentKey, language.code, extraInfo, userInfo, shouldCloseOnUnmount]);

  return {
    openChat: openZendeskChat,
    closeChat: closeZendeskChat,
  };
};
